import React, { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../ui/Button';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { TagInput } from '../ui/TagInput';
import { Sheet, Select } from './Sheet';

import ImageUpload from './ImageUpload';
import CellMediaUploader from './CellMediaUploader';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { assignableNetworks, isAdmin } from '../../lib/cellPermissions';
import { cn } from '../../lib/utils';

const PALETTE = [
  '#0284c7', '#f59e0b', '#0369a1', '#d97706', '#059669', '#7c3aed', '#e11d48',
  '#0ea5e9', '#f97316', '#14b8a6', '#8b5cf6', '#ef4444', '#22c55e', '#eab308',
  '#06b6d4', '#a855f7', '#ec4899', '#84cc16', '#f43f5e', '#3b82f6', '#10b981',
  '#facc15', '#6366f1', '#d946ef', '#0d9488', '#b45309', '#1d4ed8', '#be123c',
];

const SAMPLE_IMAGES = [
  'https://images.unsplash.com/photo-1780946101636-00004978f8c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  'https://images.pexels.com/photos/34595035/pexels-photo-34595035.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=800',
  'https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
];

function SubmitBtn({ loading, editing, testid }) {
  return (
    <Button type="submit" className="mt-2 w-full" disabled={loading} data-testid={testid}>
      {loading ? <Loader2 size={18} className="animate-spin" /> : editing ? 'Salvar alterações' : 'Criar'}
    </Button>
  );
}

// ----------------------------- REDE / NETWORK -----------------------------
export function NetworkDialog({ network, onClose, onSaved }) {
  const editing = !!network;
  const [f, setF] = useState({
    name: network?.name || '',
    color: network?.color || PALETTE[0],
    supName: network?.supervisor?.name || '',
    supPhone: network?.supervisor?.phone || '',
    mission: network?.mission || '',
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!f.name.trim() || !f.supName.trim()) return toast.error('Preencha o nome da rede e do supervisor.');
    setLoading(true);
    try {
      const supervisor = {
        ...(network?.supervisor || { id: `sup-${Date.now()}`, type: 'casal', avatar: 'https://i.pravatar.cc/240?img=11', avatar2: 'https://i.pravatar.cc/240?img=45' }),
        name: f.supName,
        phone: f.supPhone,
      };
      if (editing) await api.updateNetwork(network.id, { name: f.name, color: f.color, supervisor, mission: f.mission });
      else await api.createNetwork({ name: f.name, color: f.color, supervisor, mission: f.mission });

      toast.success(editing ? 'Rede atualizada!' : 'Rede criada!');
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title={editing ? 'Editar Rede' : 'Nova Rede'} onClose={onClose} testid="network-dialog">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Nome da rede" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Rede Efrata" data-testid="network-form-name" />
        <div>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">Cor</span>
          <div className="flex flex-wrap gap-2" data-testid="network-form-colors">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setF({ ...f, color: c })}
                style={{ backgroundColor: c }}
                className={`h-9 w-9 rounded-full transition-transform duration-200 ${f.color === c ? 'ring-2 ring-offset-2 ring-ink scale-110' : 'hover:scale-105'}`}
                aria-label={`cor ${c}`}
              />
            ))}
          </div>
        </div>
        <Input label="Missão apoiada" value={f.mission} onChange={(e) => setF({ ...f, mission: e.target.value })} placeholder="Ex.: APAC Caratinga" data-testid="network-form-mission" />
        <Input label="Supervisor(es)" value={f.supName} onChange={(e) => setF({ ...f, supName: e.target.value })} placeholder="Marcos & Priscila" data-testid="network-form-supervisor" />
        <Input label="Telefone" value={f.supPhone} onChange={(e) => setF({ ...f, supPhone: e.target.value })} placeholder="33 99999-9999" />

        <SubmitBtn loading={loading} editing={editing} testid="network-form-submit" />
      </form>
    </Sheet>
  );
}

// ----------------------------- CÉLULA / CELL -----------------------------
const CELL_STEPS = [
  { id: 1, label: 'Dados Gerais' },
  { id: 2, label: 'Liderança & Anfitrião' },
  { id: 3, label: 'Galeria de Fotos' },
];

function StepIndicator({ step, onJump, canJumpTo3 }) {
  return (
    <div className="mb-4 flex items-center gap-1.5" data-testid="cell-form-steps">
      {CELL_STEPS.map((s, i) => {
        const disabled = s.id === 3 && !canJumpTo3;
        const active = step === s.id;
        const done = step > s.id;
        return (
          <React.Fragment key={s.id}>
            {i > 0 && <div className={cn('h-0.5 flex-1', done || active ? 'bg-primary' : 'bg-slate-200')} />}
            <button
              type="button"
              disabled={disabled}
              onClick={() => onJump(s.id)}
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
                active ? 'bg-primary text-white' : done ? 'bg-primary-light text-primary' : 'bg-slate-100 text-ink-faint',
                disabled && 'cursor-not-allowed opacity-50',
              )}
              aria-label={s.label}
              title={s.label}
            >
              {done ? <Check size={14} /> : s.id}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const DAYS = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'];

export function CellDialog({ cell, networks, onClose, onSaved }) {
  const { auth } = useAuth();
  const user = auth?.user;
  const [record, setRecord] = useState(cell || null);
  const editing = !!record;
  const [step, setStep] = useState(1);
  const availableNetworks = assignableNetworks(user, networks);
  const [f, setF] = useState({
    name: cell?.name || '',
    networkId: cell?.networkId || availableNetworks[0]?.id || networks[0]?.id || '',
    day: cell?.day || 'Terça-feira',
    time: cell?.time || '19:30',
    active: cell?.active ?? true,
    street: cell?.address?.street || '',
    number: cell?.address?.number || '',
    neighborhood: cell?.address?.neighborhood || '',
    leaderName: cell?.leader?.name || '',
    leaderPhone: cell?.leader?.phone || '',
    hostName: cell?.host?.name || '',
    hostPhone: cell?.host?.phone || '',
  });
  const [loading, setLoading] = useState(false);

  function buildPayload() {
    return {
      name: f.name,
      networkId: f.networkId,
      day: f.day,
      time: f.time,
      active: f.active,
      address: {
        street: f.street, number: f.number, neighborhood: f.neighborhood,
        city: 'Caratinga',
        lat: record?.address?.lat ?? -19.79,
        lng: record?.address?.lng ?? -42.13,
      },
      leader: {
        type: record?.leader?.type || 'individual',
        name: f.leaderName, phone: f.leaderPhone,
        avatar: record?.leader?.avatar || 'https://i.pravatar.cc/240?img=50',
        ...(record?.leader?.avatar2 ? { avatar2: record.leader.avatar2 } : {}),
      },
      host: {
        name: f.hostName || f.leaderName, phone: f.hostPhone || f.leaderPhone,
        avatar: record?.host?.avatar || 'https://i.pravatar.cc/240?img=51',
      },
    };
  }

  async function saveAndContinue(e) {
    e.preventDefault();
    if (!f.name.trim() || !f.leaderName.trim() || !f.neighborhood.trim())
      return toast.error('Preencha nome, líder e bairro.');
    setLoading(true);
    try {
      const payload = buildPayload();
      const saved = editing ? await api.updateCell(record.id, payload) : await api.createCell(payload);
      setRecord(saved);
      toast.success(editing ? 'Célula atualizada!' : 'Célula criada! Agora adicione as fotos.');
      onSaved();
      setStep(3);
    } catch (err) {
      toast.error(err.message || 'Não foi possível salvar a célula.');
    } finally {
      setLoading(false);
    }
  }

  function finish() {
    onSaved();
    onClose();
  }

  return (
    <Sheet title={editing ? 'Editar Célula' : 'Nova Célula'} onClose={onClose} testid="cell-dialog">
      <StepIndicator step={step} onJump={(s) => (s !== 3 || record) && setStep(s)} canJumpTo3={!!record} />

      {step === 1 && (
        <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className="space-y-3">
          <Input label="Nome da célula" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cell-form-name" />
          <Select
            label="Rede / Presbitério"
            value={f.networkId}
            onChange={(e) => setF({ ...f, networkId: e.target.value })}
            options={availableNetworks.map((n) => ({ value: n.id, label: n.name }))}
            testid="cell-form-network"
          />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Dia" value={f.day} onChange={(e) => setF({ ...f, day: e.target.value })} options={DAYS.map((d) => ({ value: d, label: d }))} testid="cell-form-day" />
            <Input label="Hora" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="cell-form-time" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Input label="Rua" value={f.street} onChange={(e) => setF({ ...f, street: e.target.value })} /></div>
            <Input label="Nº" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} />
          </div>
          <Input label="Bairro" value={f.neighborhood} onChange={(e) => setF({ ...f, neighborhood: e.target.value })} data-testid="cell-form-neighborhood" />
          {isAdmin(user) && (
            <Checkbox label="Célula ativa" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
          )}
          <Button type="submit" className="mt-2 w-full" data-testid="cell-form-next-1">Próximo</Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={saveAndContinue} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Líder" value={f.leaderName} onChange={(e) => setF({ ...f, leaderName: e.target.value })} data-testid="cell-form-leader" />
            <Input label="Tel. líder" value={f.leaderPhone} onChange={(e) => setF({ ...f, leaderPhone: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Anfitrião" value={f.hostName} onChange={(e) => setF({ ...f, hostName: e.target.value })} />
            <Input label="Tel. anfitrião" value={f.hostPhone} onChange={(e) => setF({ ...f, hostPhone: e.target.value })} />
          </div>
          <div className="flex gap-2.5">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>Voltar</Button>
            <Button type="submit" className="flex-1" disabled={loading} data-testid="cell-form-submit">
              {loading ? <Loader2 size={18} className="animate-spin" /> : editing ? 'Salvar e continuar' : 'Criar e continuar'}
            </Button>
          </div>
        </form>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {record ? (
            <>
              <CellMediaUploader
                testid="cell-media-leader"
                cellId={record.id}
                category="leader"
                photos={record.leaderPhotos || []}
                limit={2}
                onChange={setRecord}
              />
              <CellMediaUploader
                testid="cell-media-host"
                cellId={record.id}
                category="host"
                photos={record.hostPhotos || []}
                limit={2}
                onChange={setRecord}
              />
              <CellMediaUploader
                testid="cell-media-meeting"
                cellId={record.id}
                category="meeting"
                photos={record.meetingPhotos || []}
                limit={5}
                onChange={setRecord}
              />
              <Button className="mt-2 w-full" onClick={finish} data-testid="cell-form-finish">Concluir</Button>
            </>
          ) : (
            <p className="text-sm text-ink-soft">Salve os dados gerais antes de enviar fotos.</p>
          )}
        </div>
      )}
    </Sheet>
  );
}

// ----------------------------- EVENTO / EVENT -----------------------------
const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'select', label: 'Seleção (lista)' },
  { value: 'checkbox', label: 'Caixas de seleção' },
];

function slugify(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/(^_|_$)/g, '');
}

export function EventDialog({ event, onClose, onSaved }) {
  const editing = !!event;
  const [f, setF] = useState({
    title: event?.title || '',
    date: (event?.date || '2026-08-01T19:00:00').slice(0, 16),
    location: event?.location || '',
    totalSlots: event?.totalSlots || 100,
    cover: event?.cover || SAMPLE_IMAGES[1],
  });
  const [fields, setFields] = useState(
    Array.isArray(event?.formFields) ? event.formFields.map((x) => ({ ...x })) : []
  );
  const [loading, setLoading] = useState(false);

  function updateField(i, patch) {
    setFields((prev) => prev.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }

  function addField() {
    setFields((prev) => [
      ...prev,
      { key: `campo_${Date.now()}`, label: '', type: 'text', required: false, options: [] },
    ]);
  }

  function removeField(i) {
    setFields((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function submit(e) {
    e.preventDefault();
    if (!f.title.trim() || !f.location.trim()) return toast.error('Preencha título e local.');
    // Normaliza os campos dinâmicos (gera key a partir do label quando vazio)
    const normalized = fields.map((field) => {
      const key = field.key || slugify(field.label) || `campo_${Date.now()}`;
      return {
        key,
        label: field.label,
        type: field.type || 'text',
        required: !!field.required,
        options: Array.isArray(field.options) ? field.options.filter(Boolean) : [],
      };
    });
    setLoading(true);
    try {
      const payload = {
        title: f.title,
        date: f.date.length === 16 ? `${f.date}:00` : f.date,
        location: f.location,
        totalSlots: Number(f.totalSlots) || 0,
        cover: f.cover,
        formLink: '',
        formFields: normalized,
      };
      if (editing) await api.updateEvent(event.id, payload);
      else await api.createEvent(payload);
      toast.success(editing ? 'Evento atualizado!' : 'Evento criado!');
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title={editing ? 'Editar Evento' : 'Novo Evento'} onClose={onClose} testid="event-dialog">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="event-form-title" />
        <Input label="Data e hora" type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="event-form-date" />
        <Input label="Local" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="event-form-location" />
        <Input label="Total de vagas" type="number" value={f.totalSlots} onChange={(e) => setF({ ...f, totalSlots: e.target.value })} data-testid="event-form-slots" />
        <div>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">Imagem de capa</span>
          <ImageUpload
            value={f.cover}
            onChange={(img) => setF({ ...f, cover: img })}
            samples={SAMPLE_IMAGES}
            testid="event-form-cover"
          />
        </div>

        {/* Campos dinâmicos do formulário de inscrição */}
        <div className="rounded-xl border border-slate-200 bg-canvas p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">
              Formulário de inscrição
            </span>
            <Button type="button" variant="ghost" className="!px-2 !py-1 text-xs" onClick={addField}>
              + Campo
            </Button>
          </div>

          {fields.length === 0 && (
            <p className="mt-2 text-xs text-ink-faint">
              Nenhum campo extra. O formulário usará apenas nome, telefone e e-mail.
            </p>
          )}

          <div className="mt-2 space-y-3">
            {fields.map((field, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-white p-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Rótulo"
                    value={field.label}
                    onChange={(e) => updateField(i, { label: e.target.value })}
                    placeholder="Ex.: Cidade"
                  />
                  <Select
                    label="Tipo"
                    value={field.type}
                    onChange={(e) => updateField(i, { type: e.target.value })}
                    options={FIELD_TYPES}
                  />
                </div>
                {(field.type === 'select' || field.type === 'checkbox') && (
                  <div className="mt-2">
                    <TagInput
                      label="Opções"
                      value={field.options || []}
                      onChange={(options) => updateField(i, { options })}
                      placeholder="Digite uma opção e pressione Enter"
                      hint="Pressione Enter ou use o botão + para adicionar cada opção."
                    />
                  </div>
                )}

                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs font-medium text-ink">
                    <input
                      type="checkbox"
                      checked={!!field.required}
                      onChange={(e) => updateField(i, { required: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    Obrigatório
                  </label>
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    className="text-xs font-semibold text-rose-500 hover:text-rose-600"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <SubmitBtn loading={loading} editing={editing} testid="event-form-submit" />
      </form>
    </Sheet>
  );
}

// ----------------------------- HOME SECTION -----------------------------
const SECTION_TYPES = [
  { value: 'hero', label: 'Banner principal (Hero)' },
  { value: 'stats', label: 'Estatísticas' },
  { value: 'nextEvent', label: 'Próximo evento' },
  { value: 'mediaFeed', label: 'Momentos da Comunidade' },
  { value: 'recentPosts', label: 'Últimas Atividades' },
  { value: 'modules', label: 'Conteúdos por Categoria' },
  { value: 'resources', label: 'Recursos Rápidos' },
  { value: 'banners', label: 'Banners de conversão' },
];

const ICON_OPTIONS = [
  { value: 'map', label: 'Mapa' },
  { value: 'network', label: 'Rede' },
  { value: 'users', label: 'Pessoas' },
  { value: 'calendar', label: 'Calendário' },
  { value: 'heart', label: 'Coração' },
  { value: 'play', label: 'Play' },
  { value: 'mic', label: 'Microfone' },
  { value: 'userPlus', label: 'Inscrição' },
  { value: 'gift', label: 'Presente' },
];

const TONE_OPTIONS = [
  { value: 'bg-primary-light text-primary', label: 'Azul claro' },
  { value: 'bg-accent-light text-accent-dark', label: 'Âmbar claro' },
  { value: 'bg-rose-50 text-rose-600', label: 'Rosa' },
  { value: 'bg-emerald-100 text-emerald-700', label: 'Verde' },
  { value: 'bg-violet-100 text-violet-700', label: 'Violeta' },
  { value: 'bg-primary-dark', label: 'Azul escuro' },
  { value: 'bg-accent-dark', label: 'Âmbar escuro' },
];

function defaultContent(type) {
  switch (type) {
    case 'hero':
      return {
        badge: 'Comunidade Águas Vivas · Caratinga',
        title: 'Sua vida em comunidade',
        highlight: 'começa aqui.',
        subtitle: 'Somos uma família que cresce junta, ora junta e transforma a cidade com o amor de Cristo.',
        searchPlaceholder: 'Buscar pregações, células, eventos...',
        popularTags: ['#Fé', '#Comunhão', '#Mensagens', '#Células', '#Adoração'],
        heroImage: SAMPLE_IMAGES[2],
      };
    case 'stats':
      return {
        cards: [
          { label: 'Células ativas', value: 'cells', icon: 'map', tone: 'bg-primary-light text-primary' },
          { label: 'Redes de células', value: 'networks', icon: 'network', tone: 'bg-accent-light text-accent-dark' },
          { label: 'Membros ativos', value: '120+', icon: 'users', tone: 'bg-emerald-100 text-emerald-700' },
          { label: 'Eventos no mês', value: '4', icon: 'calendar', tone: 'bg-violet-100 text-violet-700' },
        ],
      };
    case 'nextEvent':
      return {};
    case 'mediaFeed':
      return { limit: 6 };
    case 'recentPosts':
      return { posts: [] };
    case 'modules':
      return { modules: [] };
    case 'resources':
      return { resources: [] };
    case 'banners':
      return { banners: [] };
    default:
      return {};
  }
}

export function HomeSectionDialog({ section, onClose, onSaved }) {
  const editing = !!section;
  const [type, setType] = useState(section?.type || 'hero');
  const [title, setTitle] = useState(section?.title || '');
  const [subtitle, setSubtitle] = useState(section?.subtitle || '');
  const [visible, setVisible] = useState(section?.visible ?? true);
  const [content, setContent] = useState(section?.content || defaultContent(section?.type || 'hero'));
  const [loading, setLoading] = useState(false);

  function setC(patch) {
    setContent((prev) => ({ ...prev, ...patch }));
  }

  function changeType(newType) {
    setType(newType);
    setContent(defaultContent(newType));
  }

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return toast.error('Preencha o título da seção.');
    setLoading(true);
    try {
      const payload = { type, title, subtitle, visible, content };
      if (editing) await api.updateHomeSection(section.id, payload);
      else await api.createHomeSection(payload);
      toast.success(editing ? 'Seção atualizada!' : 'Seção criada!');
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title={editing ? 'Editar Seção' : 'Nova Seção'} onClose={onClose} testid="home-section-dialog">
      <form onSubmit={submit} className="space-y-3">
        <Select
          label="Tipo de seção"
          value={type}
          onChange={(e) => changeType(e.target.value)}
          options={SECTION_TYPES}
          testid="home-section-form-type"
        />
        <Input label="Título" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Momentos da Comunidade" data-testid="home-section-form-title" />
        <Input label="Subtítulo" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Descrição curta da seção" />

        {/* Campos específicos por tipo */}
        {type === 'hero' && (
          <>
            <Input label="Badge" value={content.badge || ''} onChange={(e) => setC({ badge: e.target.value })} />
            <Input label="Título principal" value={content.title || ''} onChange={(e) => setC({ title: e.target.value })} />
            <Input label="Destaque (cor acento)" value={content.highlight || ''} onChange={(e) => setC({ highlight: e.target.value })} />
            <Input label="Subtítulo" value={content.subtitle || ''} onChange={(e) => setC({ subtitle: e.target.value })} />
            <Input label="Placeholder da busca" value={content.searchPlaceholder || ''} onChange={(e) => setC({ searchPlaceholder: e.target.value })} />
            <Input label="Tags populares (separadas por vírgula)" value={(content.popularTags || []).join(', ')} onChange={(e) => setC({ popularTags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })} />
            <div>
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">Imagem de fundo</span>
              <ImageUpload value={content.heroImage || ''} onChange={(img) => setC({ heroImage: img })} samples={SAMPLE_IMAGES} testid="home-section-form-hero-image" />
            </div>
          </>
        )}

        {type === 'stats' && (
          <div className="space-y-2">
            <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">Cards de estatística</span>
            {(content.cards || []).map((card, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-canvas p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input label="Rótulo" value={card.label} onChange={(e) => {
                    const cards = [...(content.cards || [])];
                    cards[i] = { ...cards[i], label: e.target.value };
                    setC({ cards });
                  }} />
                  <Input label="Valor" value={card.value} onChange={(e) => {
                    const cards = [...(content.cards || [])];
                    cards[i] = { ...cards[i], value: e.target.value };
                    setC({ cards });
                  }} />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Select
                    label="Ícone"
                    value={card.icon}
                    onChange={(e) => {
                      const cards = [...(content.cards || [])];
                      cards[i] = { ...cards[i], icon: e.target.value };
                      setC({ cards });
                    }}
                    options={ICON_OPTIONS}
                  />
                  <Select
                    label="Cor"
                    value={card.tone}
                    onChange={(e) => {
                      const cards = [...(content.cards || [])];
                      cards[i] = { ...cards[i], tone: e.target.value };
                      setC({ cards });
                    }}
                    options={TONE_OPTIONS}
                  />
                </div>
              </div>
            ))}
            <Button type="button" variant="ghost" className="w-full" onClick={() => setC({ cards: [...(content.cards || []), { label: '', value: '', icon: 'map', tone: 'bg-primary-light text-primary' }] })}>
              + Adicionar card
            </Button>
          </div>
        )}

        {type === 'mediaFeed' && (
          <Input label="Limite de itens" type="number" value={content.limit || 6} onChange={(e) => setC({ limit: Number(e.target.value) })} />
        )}

        {type === 'recentPosts' && (
          <div className="rounded-xl border border-slate-200 bg-canvas p-3 text-xs text-ink-soft">
            Os posts são gerenciados em "Pregações". Esta seção exibe os últimos posts automaticamente.
          </div>
        )}

        {type === 'modules' && (
          <div className="rounded-xl border border-slate-200 bg-canvas p-3 text-xs text-ink-soft">
            Os módulos são configurados no código. Edite o conteúdo JSON para personalizar.
          </div>
        )}

        {type === 'resources' && (
          <div className="rounded-xl border border-slate-200 bg-canvas p-3 text-xs text-ink-soft">
            Os recursos são configurados no código. Edite o conteúdo JSON para personalizar.
          </div>
        )}

        {type === 'banners' && (
          <div className="rounded-xl border border-slate-200 bg-canvas p-3 text-xs text-ink-soft">
            Os banners são configurados no código. Edite o conteúdo JSON para personalizar.
          </div>
        )}

        <label className="flex items-center gap-2 text-sm font-medium text-ink">
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
          Visível na Home
        </label>

        <SubmitBtn loading={loading} editing={editing} testid="home-section-form-submit" />
      </form>
    </Sheet>
  );
}

// ----------------------------- FOTO / GALLERY -----------------------------
export function GalleryDialog({ item, onClose, onSaved }) {

  const editing = !!item;
  const [f, setF] = useState({
    title: item?.title || '',
    category: item?.category || 'celula',
    img: item?.img || SAMPLE_IMAGES[0],
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!f.title.trim()) return toast.error('Preencha o título da foto.');
    setLoading(true);
    try {
      if (editing) await api.updateGalleryItem(item.id, f);
      else await api.createGalleryItem(f);
      toast.success(editing ? 'Foto atualizada!' : 'Foto adicionada!');
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title={editing ? 'Editar Foto' : 'Adicionar Foto'} onClose={onClose} testid="gallery-dialog">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">Foto</span>
          <ImageUpload
            value={f.img}
            onChange={(img) => setF({ ...f, img })}
            samples={SAMPLE_IMAGES}
            testid="gallery-form-image"
          />
        </div>
        <Input label="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="gallery-form-title" />
        <Select
          label="Categoria"
          value={f.category}
          onChange={(e) => setF({ ...f, category: e.target.value })}
          options={[
            { value: 'celula', label: 'Célula' },
            { value: 'rede', label: 'Rede' },
            { value: 'evento', label: 'Evento' },
          ]}
          testid="gallery-form-category"
        />
        <SubmitBtn loading={loading} editing={editing} testid="gallery-form-submit" />
      </form>
    </Sheet>
  );
}

// ----------------------------- PREGAÇÃO / PREACHING -----------------------------
const thumbUrl = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

export function PreachingDialog({ preaching, channels, onClose, onSaved }) {
  const editing = !!preaching;
  const [f, setF] = useState({
    title: preaching?.title || '',
    channelId: preaching?.channelId || channels[0]?.id || '',
    preacher: preaching?.preacher || '',
    date: preaching?.date || '2026-08-01',
    duration: preaching?.duration || '',
    views: preaching?.views || 0,
    videoId: preaching?.videoId || '',
    series: preaching?.series || '',
    description: preaching?.description || '',
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!f.title.trim() || !f.videoId.trim()) return toast.error('Preencha título e ID do vídeo.');
    setLoading(true);
    try {
      const payload = {
        title: f.title,
        channelId: f.channelId,
        preacher: f.preacher,
        date: f.date,
        duration: f.duration,
        views: Number(f.views) || 0,
        videoId: f.videoId.trim(),
        series: f.series,
        description: f.description,
      };
      if (editing) await api.updatePreaching(preaching.id, payload);
      else await api.createPreaching(payload);
      toast.success(editing ? 'Pregação atualizada!' : 'Pregação criada!');
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title={editing ? 'Editar Pregação' : 'Nova Pregação'} onClose={onClose} testid="preaching-dialog">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="preaching-form-title" />
        <Select
          label="Canal"
          value={f.channelId}
          onChange={(e) => setF({ ...f, channelId: e.target.value })}
          options={channels.map((c) => ({ value: c.id, label: c.name }))}
          testid="preaching-form-channel"
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Pregador" value={f.preacher} onChange={(e) => setF({ ...f, preacher: e.target.value })} data-testid="preaching-form-preacher" />
          <Input label="Data" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="preaching-form-date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Duração" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="48:12" />
          <Input label="Visualizações" type="number" value={f.views} onChange={(e) => setF({ ...f, views: e.target.value })} />
        </div>
        <Input label="ID do vídeo (YouTube)" value={f.videoId} onChange={(e) => setF({ ...f, videoId: e.target.value })} placeholder="dQw4w9WgXcQ" data-testid="preaching-form-videoid" />
        {f.videoId && (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <img src={thumbUrl(f.videoId)} alt="Preview do vídeo" className="aspect-video w-full object-cover" />
          </div>
        )}
        <Input label="Série" value={f.series} onChange={(e) => setF({ ...f, series: e.target.value })} placeholder="Fundamentos da Fé" />
        <Input label="Descrição" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <SubmitBtn loading={loading} editing={editing} testid="preaching-form-submit" />
      </form>
    </Sheet>
  );
}

// ----------------------------- CANAL / CHANNEL -----------------------------
export function ChannelDialog({ channel, onClose, onSaved }) {
  const editing = !!channel;
  const [f, setF] = useState({
    name: channel?.name || '',
    handle: channel?.handle || '',
    url: channel?.url || '',
    description: channel?.description || '',
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!f.name.trim() || !f.url.trim()) return toast.error('Preencha nome e URL do canal.');
    setLoading(true);
    try {
      const payload = { name: f.name, handle: f.handle, url: f.url, description: f.description };
      if (editing) await api.updatePreachingChannel(channel.id, payload);
      else await api.createPreachingChannel(payload);
      toast.success(editing ? 'Canal atualizado!' : 'Canal criado!');
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title={editing ? 'Editar Canal' : 'Novo Canal'} onClose={onClose} testid="channel-dialog">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Caratinga" data-testid="channel-form-name" />
        <Input label="Handle" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value })} placeholder="@aguasvivascomunidade" />
        <Input label="URL do canal" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://www.youtube.com/..." data-testid="channel-form-url" />
        <Input label="Descrição" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <SubmitBtn loading={loading} editing={editing} testid="channel-form-submit" />
      </form>
    </Sheet>
  );
}


